import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { developmentApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface ChangeStatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (newStatus: string) => void;
    target: { id: number; status: string; title: string; code: string } | null;
}

export const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    target,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isCancelled = target?.status === "cancelled";
    const newStatus = isCancelled ? "development" : "cancelled";

    const handleSubmit = async () => {
        if (!target) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await developmentApi.update(target.id, {
                status: newStatus,
            });
            if (res.result) {
                showSuccess(t("developments.modal_status.success"));
                onSuccess(newStatus);
            } else {
                setError(res.message || t("developments.modal_status.error"));
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.modal_status.error")
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (!target) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                isCancelled
                    ? t("developments.modal_status.title_reopen")
                    : t("developments.modal_status.title_cancel")
            }
            submitLabel={
                isCancelled
                    ? t("developments.modal_status.btn_reopen")
                    : t("developments.modal_status.btn_cancel")
            }
            cancelLabel={t("modal.close")}
            onSubmit={handleSubmit}
            isSubmitting={submitting}
            submitVariant={isCancelled ? "primary" : "danger"}
        >
            <div className="space-y-4">
                {error && (
                    <div className="p-3 bg-rose-50 text-rose-600 rounded-xl text-sm">
                        {error}
                    </div>
                )}
                <p className="text-center text-sm text-slate-600 dark:text-zinc-400">
                    {isCancelled
                        ? t("developments.modal_status.desc_reopen")
                        : t("developments.modal_status.desc_cancel")}
                </p>

                <div className="text-center">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                        {target.code}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-zinc-400 mt-1">
                        {target.title}
                    </p>
                </div>
            </div>
        </Modal>
    );
};
